import type {FC} from 'react';
import {useState, useEffect} from 'react';
import moment from 'moment';
import {useMutation} from '@tanstack/react-query';
import {groupsAdmissionsCreate} from 'api/v2';
import type {
  Group,
  GroupAdmissionCreateRequest,
  ProgramAdmissionRead,
} from 'api/v2';
import {
  Box,
  Button,
  DatePicker,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {SelectMemberList} from './SelectMemberList';
import {pluralize} from 'utils/Utils';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {SearchInput} from 'components/SearchInput';

interface AddMemberToGroupProps {
  group: Group;
  closeModal: () => void;
  groupMemberListRefetch: () => void;
}

export const AddMemberToGroup: FC<AddMemberToGroupProps> = ({
  group,
  closeModal,
  groupMemberListRefetch,
}) => {
  const [rowsToAdd, setRowsToAdd] = useState<Array<ProgramAdmissionRead>>([]);
  const [startDate, setStartDate] = useState<moment.Moment>(moment());
  const [endDate, setEndDate] = useState<moment.Moment>(
    moment().add(90, 'days'),
  );
  const [isValidStartDate, setIsValidStartDate] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const addMembers = useMutation({
    mutationFn: (newMembers: GroupAdmissionCreateRequest) => {
      setIsSubmitting(true);
      return groupsAdmissionsCreate(group.id, newMembers);
    },
  });

  useEffect(() => {
    const isValid = rowsToAdd.every((row) =>
      startDate.isSameOrAfter(row.startDate, 'day'),
    );
    setIsValidStartDate(isValid);
  }, [rowsToAdd, startDate]);

  const handleSubmit = () => {
    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    const request: GroupAdmissionCreateRequest = rowsToAdd.map((row) => ({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      group: group.id,
      member: row.member.id,
      organization: row.organization,
    }));

    addMembers.mutate(request, {
      onSuccess: () => {
        groupMemberListRefetch();
        closeModal();
      },
      onError: () => {
        showAlert(
          'Something went wrong.  Please try again.',
          ALERT_TYPES.ERROR,
        );
      },
      onSettled: () => setIsSubmitting(false),
    });
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', height: '60vh'}}>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={4}
        sx={{pt: 1, pb: 2}}
        alignItems="flex-start"
        justifyContent="space-between">
        <SearchInput onChange={setSearchTerm} label="Search for members" />

        <Box>
          <Stack spacing={2} direction="row">
            <DatePicker
              label="Start Date"
              fieldName="startDate"
              onChange={setStartDate}
              value={startDate.format('YYYY-MM-DD')}
              error={!isValidStartDate}
              inputProps={{
                sx: {
                  minWidth: 90,
                },
              }}
            />

            <DatePicker
              label="End Date"
              fieldName="endDate"
              onChange={setEndDate}
              value={endDate.format('YYYY-MM-DD')}
              minDate={startDate.format('YYYY-MM-DD')}
              inputProps={{
                sx: {
                  minWidth: 90,
                },
              }}
            />
          </Stack>
          {!isValidStartDate && (
            <Typography sx={{pt: 1}} color="error" variant="body2">
              Group start date must be after the Program start date for all
              selected members
            </Typography>
          )}
        </Box>
      </Stack>

      <SelectMemberList
        groupId={group.id}
        programId={group.programs[0]}
        searchTerm={searchTerm}
        selectedRows={rowsToAdd}
        setSelectedRows={setRowsToAdd}
      />

      <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{mt: 2}}>
        <Button onClick={closeModal}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!rowsToAdd.length || !isValidStartDate || isSubmitting}
          onClick={handleSubmit}>
          Add {pluralize('Member', rowsToAdd.length)}
        </Button>
      </Stack>
    </Box>
  );
};
